import { api } from "./api";
import { edgeGet } from "./edgeApi";
import { refereeMutate } from "./refereeApi";

// Phase 83z.61 — Helper that pipes a referee mutation through the
// offline-resilient layer. Returns the server `data` on success, or a
// synthetic stub when the mutation was queued (so existing callers that
// use the response object don't crash; they should observe `queued:true`
// to switch to optimistic UI if they need to).
function _mut(match_id, endpoint, method, body) {
  return refereeMutate({ match_id, endpoint, method, body }).then((res) => {
    if (res.queued) {
      return { _queued: true, _offline: !!res.offline, _event_id: res.event_id };
    }
    return res.data;
  });
}

export const publicApi = {
  // Phase 82a — list tournaments cho athlete homepage / tournament list
  // Đợt 2 edge (08/2026) — qua Worker api-edge (policy t_list 30s).
  listTournaments: (params = {}) =>
    edgeGet("/public/tournaments", { policyKey: "t_list", params }).then((r) => r.data),
  getTournament: (slug) => edgeGet(`/public/tournaments/${slug}`, { policyKey: "t_detail" }).then((r) => r.data),
  register: (slug, eventId, payload) =>
    api.post(`/public/tournaments/${slug}/events/${eventId}/register`, payload).then((r) => r.data),
  getRegistration: (regId) => api.get(`/public/registrations/${regId}`).then((r) => r.data),
  getGroupStageBySlug: (slug, eventId) =>
    api.get(`/public/tournaments/${slug}/events/${eventId}/group-stage`).then((r) => r.data),
  getGroupStageByEvent: (eventId) =>
    api.get(`/public/events/${eventId}/group-stage`).then((r) => r.data),
  // Phase 83b — list event registrations (Athlete view)
  listEventRegistrations: (slugOrId, eventId, params = {}) =>
    api.get(`/public/tournaments/${slugOrId}/events/${eventId}/registrations`, { params })
      .then((r) => r.data),
};

export const refereeApi = {
  myMatches: (status) =>
    api.get("/referee/matches", { params: status ? { status } : {} }).then((r) => r.data),
  getMatch: (id) => api.get(`/referee/matches/${id}`, { timeout: 6000 }).then((r) => r.data),
  setScore: (id, setNo, scoreA, scoreB) =>
    _mut(id, `/api/referee/matches/${id}/score`, "PATCH", { set_no: setNo, score_a: scoreA, score_b: scoreB }),
  complete: (id) => _mut(id, `/api/referee/matches/${id}/complete`, "POST", {}),
  reopen: (id) => _mut(id, `/api/referee/matches/${id}/reopen`, "POST", {}),
  // Phase 43 — Karinox-grade
  byTournament: (tid) => api.get(`/referee/by-tournament/${tid}`).then((r) => r.data),
  stats: () => api.get(`/referee/stats`).then((r) => r.data),
  createAdhoc: (payload) => api.post(`/referee/adhoc-matches`, payload).then((r) => r.data),
  startMatch: (id, payload) => _mut(id, `/api/referee/matches/${id}/start`, "POST", payload),
  // Phase 76 — Coin toss
  coinToss: (id, payload) => _mut(id, `/api/referee/matches/${id}/coin-toss`, "POST", payload),
  resetCoinToss: (id, reason) => _mut(id, `/api/referee/matches/${id}/coin-toss/reset`, "POST", { reason }),
  pauseMatch: (id, payload) => _mut(id, `/api/referee/matches/${id}/pause`, "POST", payload),
  resumeMatch: (id) => _mut(id, `/api/referee/matches/${id}/resume`, "POST", {}),
  rally: (id, winningTeam) => _mut(id, `/api/referee/matches/${id}/rally`, "POST", { winning_team: winningTeam }),
  undo: (id) => _mut(id, `/api/referee/matches/${id}/undo`, "POST", {}),
  switchSides: (id) => _mut(id, `/api/referee/matches/${id}/switch-sides`, "POST", {}),
  // Phase 81i — Timeout 2 loại: consultation (60s) | medical (5 phút).
  // Mỗi đội có quota 1 lượt mỗi loại. Backend tracks active_timeout +
  // timeline entries (started_at, ended_at, kind).
  timeout: (id, team, kind = "consultation") =>
    _mut(id, `/api/referee/matches/${id}/timeout`, "POST", { team, kind }),
  endTimeout: (id, timeoutId) =>
    _mut(id, `/api/referee/matches/${id}/timeout/end`, "POST", { timeout_id: timeoutId }),
  card: (id, payload) => _mut(id, `/api/referee/matches/${id}/card`, "POST", payload),
  note: (id, text) => _mut(id, `/api/referee/matches/${id}/note`, "POST", { text }),
  support: (id, reason) => _mut(id, `/api/referee/matches/${id}/support`, "POST", { reason }),
  endEarly: (id, payload) => _mut(id, `/api/referee/matches/${id}/end-early`, "POST", payload),
  // v16.17 Phase C.22 — Chọn lại sân + bên giao đỡ (max 2 lần / trận).
  reselectSetup: (id) => _mut(id, `/api/referee/matches/${id}/reselect-setup`, "POST", {}),
  // v16.17 Phase C.23 — Clear pending_completion khi referee bấm "Không, quay lại"
  clearPendingCompletion: (id) => _mut(id, `/api/referee/matches/${id}/clear-pending-completion`, "POST", {}),
  // Phase 81m — Bỏ cuộc: { winning_team: "A"|"B", reason: string }
  forfeit: (id, payload) => _mut(id, `/api/referee/matches/${id}/forfeit`, "POST", payload),
  // Phase 88.B — Heartbeat ping (mỗi 30s khi đang chấm). BE scanner phát hiện
  // no-ping >120s → Telegram alert "Trọng tài thật mất kết nối".
  heartbeat: (id, tournamentId, online = true) =>
    api.post(`/referee/heartbeat`, { match_id: id, tournament_id: tournamentId, online }).then((r) => r.data),
};

export const dashboardApi = {
  stats: () => api.get("/admin/dashboard/stats").then((r) => r.data),
};

export const adminMatchesApi = {
  assign: (id, payload) => api.patch(`/admin/matches/${id}/assign`, payload).then((r) => r.data),
};
